import {
  DEFAULT_TABLE_HEADER_BACKGROUND_COLOR,
  DEFAULT_TABLE_HEADER_TEXT_COLOR,
  GROUP_COLUMN_PREFIX,
  NULL_GROUPING,
} from '../Constants';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import toString from 'lodash/toString';
import union from 'lodash/union';
import { CellClassParams, ColDef, IRowNode } from '@ag-grid-community/core';
import { CustomAgGridContext } from 'common/types/agGrid/context';
import { EXPORT_TIME_FORMATS } from 'common/DataTypeFormatter';
import { FormatStyle, TableColumnFormat } from '../types';
import { RowStripeStyle } from 'common/types/agGrid/rowStripe';
import { SoQLType } from 'common/types/soql';
import { ViewColumn } from 'common/types/viewColumn';
import { defaultRowStripeValue } from 'common/authoring_workflow/components/panes/PresentationPane/RowStripeSelector';
import { generateDecimalFormat, generateIntegerFormat } from './xlsxExportCellStylesHelper';
import {
  getConfigClassIndex,
  isWithSubtotalLabel,
  shouldApplyDateFormattingClass,
  getNumberPrecisionInfo,
} from './xlsxExportCellClassesHelper';
import { isColumnStylingInTablesEnabled } from 'common/visualizations/helpers/VifSelectors';
import { CALENDAR_DATE_COLUMN_TYPE, NUMBER_COLUMN_TYPE } from 'common/authoring_workflow/constants';
import { getFieldName } from './getFieldName';
import { isColDefPivoted } from './isColDefPivoted';
import { isNormalCell, isSubtotalCell } from './isTotalCell';

export interface IExcelStylesParams {
  id: string;
  alignment?: { horizontal: string; wrapText?: boolean };
  font?: { color?: string; italic?: boolean; bold?: boolean };
  interior?: { color: string; pattern: string };
  dataType?: string;
  numberFormat?: { format: string };
}

interface ITableExportFormattingParams {
  columnFormats: { [key: string]: TableColumnFormat };
  rowStripeStyle: RowStripeStyle | undefined;
  columnMetadata: ViewColumn[];
  headerFormat: FormatStyle;
}

/**  excelStyles in gridOptions expects an array of style objects, each of which should include
 * an id property (which corresponds to the className) that matches the values generated by generateCellClasses.
 * @returns ```
 * [
 *   {
 *       id: "style-option-<fieldname>-",
 *       font: { color: "#e0ffc1"},
 *       interior: {
 *          color: "#008000", pattern: 'Solid'
 *       }
 *   }
 * ]```;
 */
export const generateExcelStyles = (params: ITableExportFormattingParams) => {
  const { headerFormat, columnMetadata, rowStripeStyle, columnFormats } = params;
  let excelStyles: IExcelStylesParams[] = generateCommonCellClasses(headerFormat);
  excelStyles = [
    ...generateDefaultStyle(columnMetadata),
    ...excelStyles,
    ...generateRowStriping(rowStripeStyle),
  ];

  // Retrieve all field names with column formatting and overview formatting
  // Currently, only number & date types are retrieved for overview formatting
  const supportedViewColumnTypes = [CALENDAR_DATE_COLUMN_TYPE, NUMBER_COLUMN_TYPE];
  const overviewFormattingFieldnames = columnMetadata
    .filter(
      (column) => !isEmpty(column.format ?? {}) && supportedViewColumnTypes.includes(column.dataTypeName),
    )
    .map((column) => column.fieldName);

  union(overviewFormattingFieldnames, Object.keys(columnFormats)).forEach((colKey) => {
    if (columnFormats[colKey]?.style) {
      excelStyles.push(generateStyleOption(colKey, styleAttributes(columnFormats[colKey]), 'style-option'));
    }

    if (columnFormats[colKey]?.isAlignHeader) {
      const alignment = columnFormats[colKey].format?.align ?? '';
      excelStyles.push(generateHeaderAlignment(alignment, headerFormat));
    }

    const viewFormat = columnFormats[colKey]?.format?.view;
    const viewColumn = columnMetadata.find((column) => column.fieldName == colKey);
    const columnMetadataViewFormat = viewColumn?.format?.view;
    if (hasDateViewFormat(columnFormats[colKey], viewColumn)) {
      const view = isUndefined(viewFormat) ? columnMetadataViewFormat : viewFormat;
      const timeFormat = EXPORT_TIME_FORMATS[view as keyof typeof EXPORT_TIME_FORMATS];

      excelStyles.push({
        id: `datetime-formatted-cell-${colKey}`,
        dataType: 'DateTime',
        numberFormat: {
          format: timeFormat,
        },
      });
    }

    if (viewColumn?.dataTypeName === SoQLType.SoQLNumberT) {
      const styleFormat = generateIntegerFormat(columnFormats[colKey]?.format, viewColumn.format);
      excelStyles.push({
        id: `number-formatted-cell-${colKey}`,
        dataType: 'Number',
        numberFormat: {
          format: styleFormat,
        },
      });
      excelStyles = [...excelStyles, ...generateDecimalFormat(styleFormat, colKey)];
    }

    const conditionalConfigs = columnFormats[colKey]?.conditionalConfig;
    conditionalConfigs?.forEach((config, index) => {
      if (index > 0) {
        const isSameCondition = isEqual(conditionalConfigs[index - 1].expression, config.expression);
        if (isSameCondition) return;
      }
      excelStyles.push(generateStyleOption(colKey, styleAttributes(config), 'conditional-config', index));
    });
  });

  return excelStyles;
};

const hasDateViewFormat = (columnFormat: TableColumnFormat, columnViewFormat: ViewColumn | undefined) => {
  const viewFormat = columnFormat?.format?.view;
  const overviewFormat = columnViewFormat?.format?.view;

  return (
    (columnFormat?.expression.function === 'timeRange' &&
      columnFormat?.format.hasOwnProperty('view') &&
      !isNil(viewFormat)) ||
    !isNil(overviewFormat)
  );
};

interface IPushColumnStyleClasses {
  params: ICellClassesParams;
  configClassIndex: number | undefined;
  columnName: string;
  styleClasses: string[];
  columnFormatItem: TableColumnFormat;
}

const pushColumnStyleClasses = ({
  configClassIndex,
  params,
  styleClasses,
  columnName,
  columnFormatItem,
}: IPushColumnStyleClasses) => {
  if (!isUndefined(configClassIndex) && configClassIndex !== -1 && !isSubtotalCell(params)) {
    styleClasses.push(`conditional-config-${columnName}-${configClassIndex}`);
  }
  if (columnFormatItem && params.value && isNormalCell(params)) {
    styleClasses.push(`style-option-${columnName}`);
  }
};

export interface ICellClassesParams {
  colFieldName: string;
  colDataTypeName: string;
  node: IRowNode;
  context: CustomAgGridContext;
  value: string | number;
  colDef?: ColDef<any, any>;
}

/**
 * `cellClass` expects an array of class names that match the ID values generated by `generateExcelStyles()`.
 * These class names are dependent on the `columnFormatting` values.
 *
 * For conditional formatting with multiple conditions, only one class should be returned—the one with the highest priority.
 *
 * For non-conditional formatting classes, there might be instances of multiple classes depending on the field type.
 *
 * Below is a simple example of the set of classes a cell may have.
 * @returns
 * ```
 * [
 *    'conditional-config-<fieldname>-<matchedConfigIndex>',
 *    'style-option-<fieldname>',
 *    'default-style-<fieldname>'
 * ];
 * ```
 */
export const generateCellClasses = (params: ICellClassesParams) => {
  const { colFieldName, context, node, colDataTypeName, value, colDef } = params;
  const styleClasses: string[] = [];
  const columnName = colFieldName.replace(`${GROUP_COLUMN_PREFIX}-`, '');
  const columnFormatItem = context.columnFormats[columnName];
  const configClassIndex = getConfigClassIndex(params, columnName);
  const isGroupedRowAndGroupedColumn = node.group && columnName === node.field;
  const isNonGroupedRowAndNonGroupedColumn = !node.group && !context.groupedColumns.includes(columnName);

  if (isGroupedRowAndGroupedColumn || isNonGroupedRowAndNonGroupedColumn || isColDefPivoted(colDef)) {
    pushColumnStyleClasses({
      configClassIndex,
      columnFormatItem,
      columnName,
      params,
      styleClasses,
    });
  }
  if (shouldApplyDateFormattingClass(params, columnName)) {
    styleClasses.push(`datetime-formatted-cell-${columnName}`);
  }

  if (
    colDataTypeName === SoQLType.SoQLNumberT &&
    value !== NULL_GROUPING &&
    !isWithSubtotalLabel(params) &&
    !(node?.isRowPinned() && toString(value) == 'Total')
  ) {
    const { isDecimal, decimalPlaces } = getNumberPrecisionInfo(params);
    if (isDecimal && decimalPlaces) {
      styleClasses.push(`decimal-${decimalPlaces}-formatted-cell-${columnName}`);
    } else {
      styleClasses.push(`number-formatted-cell-${columnName}`);
    }
  }

  styleClasses.push(`default-style-${columnName}`);
  const rowStripeStyle = node.rowIndex && node.rowIndex % 2 !== 0 ? 'alternate-row' : 'base-row';
  styleClasses.push(rowStripeStyle);
  return styleClasses;
};

export const tableExportRenderer = (colDef: ColDef<any, any> | undefined, rendererParams: any) => {
  return typeof colDef?.cellRenderer === 'string'
    ? colDef?.cellRendererParams.innerRenderer(rendererParams)
    : colDef?.cellRenderer(rendererParams);
};

const generateRowStriping = (rowStripeStyle: RowStripeStyle | undefined) => {
  if (!isNil(rowStripeStyle) && !isEmpty(rowStripeStyle)) {
    return ['base', 'alternate'].map((type) => {
      const stripeStyle = rowStripeStyle[type as keyof RowStripeStyle];
      const defaultStyle = defaultRowStripeValue[type as keyof RowStripeStyle];
      const style = {
        id: `${type}-row`,
        font: {
          color: stripeStyle?.text || '#000',
        },
      };

      if (!isEqual(stripeStyle?.fill, defaultStyle?.fill)) {
        const interiorStyle = {
          interior: {
            color: stripeStyle?.fill || '#fff',
            pattern: 'Solid',
          },
        };
        return { ...style, ...interiorStyle };
      } else {
        return style;
      }
    });
  }
  return [];
};

const generateDefaultStyle = (viewColumns: ViewColumn[]) => {
  return viewColumns.map((viewColumn: ViewColumn) => {
    let alignment = 'Left';
    if (viewColumn.renderTypeName === SoQLType.SoQLNumberT) {
      alignment = 'Right';
    } else if ([SoQLType.SoQLBooleanT, SoQLType.SoQLBooleanAltT].includes(viewColumn.renderTypeName)) {
      alignment = 'Center';
    } else if (viewColumn.renderTypeName === SoQLType.SoQLFloatingTimestampT) {
      return {
        id: `datetime-formatted-cell-${viewColumn.fieldName}`,
        dataType: 'DateTime',
        numberFormat: {
          format: EXPORT_TIME_FORMATS.default,
        },
        alignment: {
          horizontal: alignment,
        },
      };
    }

    return {
      id: `default-style-${viewColumn.fieldName}`,
      alignment: {
        horizontal: alignment,
      },
    };
  });
};

const generateStyleOption = (colKey: string, styles: any, prefix: string, index?: number) => {
  const id = typeof index === 'number' ? `${prefix}-${colKey}-${index}` : `${prefix}-${colKey}`;
  const baseStyleOption = {
    id: id,
  };
  const stylingOption = {
    font: {
      color: styles.fontColor,
      bold: styles.bold,
      italic: styles.italic,
    },
    interior: {
      color: styles.backgroundColor,
      pattern: 'Solid',
    },
  };

  if (prefix === 'style-option') {
    return isColumnStylingInTablesEnabled() ? { ...baseStyleOption, ...stylingOption } : baseStyleOption;
  }

  return { ...baseStyleOption, ...stylingOption };
};

const styleAttributes = (formatObj: any) => {
  return {
    fontColor: formatObj.style?.textColor,
    backgroundColor: formatObj.style?.backgroundColor,
    bold: formatObj.style?.fontStyle?.isBold,
    italic: formatObj.style?.fontStyle?.isItalic,
  };
};

const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/** headerClass in columnDef returns an array of class names - ag-grid-header included.
 * We need to use the class name - 'ag-grid-header' to match the value from columnDef headerClass
 * we don't need multiple class names since the style is same for all column headers.
 * returns a value with type IExcelStylesParams
 * sample returned value: ```
 * { id: 'ag-grid-header', font: { color: <value>, bold: <value>, italic: <value>}, interior: { color: <value>, pattern: 'Solid' }} ```;
 */
const generateHeaderStyle = (headerFormat: FormatStyle) => {
  const isBoldHeaderFontStyle = headerFormat?.fontStyle?.isBold ?? true;
  const isItalicHeaderFontStyle = headerFormat?.fontStyle?.isItalic ?? false;
  const textColor = headerFormat.textColor ?? DEFAULT_TABLE_HEADER_TEXT_COLOR;
  const backgroundColor = headerFormat.backgroundColor ?? DEFAULT_TABLE_HEADER_BACKGROUND_COLOR;
  const style = {
    id: 'ag-grid-header',
    font: {
      color: textColor,
      bold: isBoldHeaderFontStyle,
      italic: isItalicHeaderFontStyle,
    },
    alignment: {
      wrapText: headerFormat?.wrapHeaders ?? false,
    },
  };
  const interiorStyle = {
    interior: {
      color: backgroundColor,
      pattern: 'Solid',
    },
  };

  if (isEqual(backgroundColor, DEFAULT_TABLE_HEADER_BACKGROUND_COLOR)) {
    return style as IExcelStylesParams;
  }

  return { ...style, ...interiorStyle } as IExcelStylesParams;
};

/** headerClass in columnDef returns an array of class names - ag-grid-header-${alignment} included.
 * We need to use that class name to match the value from columnDef headerClass
 * returns a value with type IExcelStylesParams
 * sample returned value: ```
 * { id: 'ag-grid-header-left', alignment: { horizontal: 'Left' }} ```;
 */
const generateHeaderAlignment = (alignment: string, headerFormat: FormatStyle) => {
  return {
    id: `ag-grid-header-${alignment}`,
    alignment: {
      horizontal: capitalize(alignment),
      wrapText: headerFormat?.wrapHeaders ?? false,
    },
  } as IExcelStylesParams;
};

const generateCommonCellClasses = (headerFormat: FormatStyle) => {
  const excelStyles: any = [];
  excelStyles.push(generateHeaderStyle(headerFormat));

  // Grand Total Row
  excelStyles.push({
    id: 'total-row',
    font: {
      bold: true,
    },
  });

  // Group Total Row
  excelStyles.push({
    id: 'subtotal-row',
    font: {
      bold: true,
    },
  });

  // Alignment
  ['Center', 'Left', 'Right'].forEach((alignment) => {
    excelStyles.push({
      id: `${alignment.toLowerCase()}-align`,
      alignment: {
        horizontal: alignment,
      },
    });
  });

  return excelStyles;
};

export const getCellClassRules = (params: CellClassParams, rule: string): boolean => {
  const fieldName = getFieldName(params);
  if (!fieldName || params.node.footer) {
    return false;
  }
  const ctx: CustomAgGridContext = params.context;
  return ctx.columnFormats[fieldName]?.format.align === rule;
};

export const generateUrlValue = (value: { url: string; description?: string }) => {
  if (!value) {
    return;
  }
  const linkValue = value.hasOwnProperty('description') ? `${value.description} (${value.url})` : value.url;
  return linkValue;
};

export const unsupportedDateFormats = ['iso_8601_date_time', 'iso_8601_date_time_utc'];
